import type { Experience } from './types';

/**
 * Dates are taken verbatim from v6.1. The TCS `roles` sub-track comes from the
 * older v4 resume, which is the only document carrying the internal promotion
 * dates; they are consistent with the v6.1 outer range (PLAN.md §3.1).
 */
export const experience: Experience[] = [
  {
    id: 'axzora',
    company: 'Axzora Resourcing LLC',
    role: 'Software Developer — Java',
    location: 'Remote — Houston, TX',
    startDate: '2025-08',
    endDate: null,
    summary:
      'Building production Spring Boot microservices for banking workflows, owned end to end from requirement analysis through deployment and post-release support.',
    tech: [
      'Java 17',
      'Spring Boot',
      'Spring Data MongoDB',
      'MongoDB',
      'REST',
      'Swagger/OpenAPI',
      'GitLab CI/CD',
      'SonarQube',
      'JUnit',
      'Selenium',
      'Kibana',
    ],
    highlights: [
      {
        // Merged from two bullets that separately described what a backend job
        // is rather than what he did. Leads with the domain so a scanner hits
        // "banking" and "transaction" in the first line of the current role.
        text: 'Build and own 4–6 Spring Boot REST microservices in Java 17 for banking transaction workflows, with Swagger/OpenAPI contracts consumed by 3–5 downstream teams and the React front end — request validation, centralized exception handling and structured logging, from requirement analysis through UAT and production support.',
        tags: ['backend', 'delivery', 'leadership'],
        lead: true,
      },
      {
        text: 'Ship changes through GitLab CI/CD across development, UAT, and production on a monthly release cadence, working in two-week sprints.',
        tags: ['devops', 'delivery'],
      },
      {
        text: 'Model and query application data in MongoDB through Spring Data MongoDB for transaction persistence and financial data integration.',
        tags: ['backend', 'data'],
      },
      {
        text: 'Harden application quality against the secure-coding standards required in banking, maintaining SonarQube quality-gate compliance with JUnit and Selenium automation.',
        tags: ['quality', 'security'],
      },
      {
        text: 'Diagnose production defects through Kibana log analysis, tracing backend service and integration failures to root cause.',
        tags: ['devops', 'quality'],
      },
      {
        text: 'Contribute to code reviews and system-design discussions across the team.',
        tags: ['leadership', 'quality'],
      },
    ],
  },
  {
    // Site only — the resume renderers filter `kind: 'education'` out of the
    // Professional Experience section, because those documents already carry a
    // full Education section listing this degree with the same dates, and
    // printing it twice on one page reads as padding.
    //
    // On the site the sections are far apart in a scroll, so the timeline entry
    // is what stops Nov 2023 - Aug 2025 looking like an unexplained gap.
    //
    // Note this makes the Aug-Nov 2023 overlap with TCS visible in one place:
    // that period was paid then sabbatical leave, resigned at the end of it.
    id: 'uta-ms',
    kind: 'education',
    company: 'The University of Texas at Arlington',
    role: 'Master of Science in Computer Science',
    location: 'Arlington, TX',
    startDate: '2023-08',
    endDate: '2025-05',
    summary:
      'Machine learning, software engineering, database systems, secure application development, and full stack development.',
    highlights: [],
    tech: [],
  },
  {
    id: 'tcs',
    company: 'Tata Consultancy Services',
    role: 'System Engineer',
    location: 'Hyderabad, India',
    startDate: '2019-07',
    endDate: '2023-11',
    client: 'FCA / Stellantis',
    summary:
      'Four years owning 10–12 services across web applications, REST APIs and Azure batch pipelines for an automotive manufacturing client, promoted twice.',
    roles: [
      { title: 'Assistant System Engineer Trainee', startDate: '2019-07', endDate: '2020-07' },
      { title: 'Assistant System Engineer', startDate: '2020-07', endDate: '2021-03' },
      { title: 'System Engineer', startDate: '2021-03', endDate: '2023-11' },
    ],
    tech: [
      'Core Java',
      'Spring Web',
      'AWS Kinesis',
      'Azure Functions',
      'Azure Data Factory',
      'Azure Storage',
      'DB2',
      'JUnit',
      'Selenium',
      'SonarQube',
      'Salesforce Marketing Cloud',
    ],
    highlights: [
      {
        text: 'Designed and built schedule-triggered batch-processing pipelines from scratch using Azure Functions, Azure Data Factory, Azure Storage, and FTP server connections, handling 200K–500K records per run.',
        tags: ['cloud', 'data', 'backend'],
      },
      // Order matters: the site shows the first four bullets and collapses the
      // rest, so the strongest evidence has to sit in that visible set. The
      // coverage story, the Kinesis event-driven work and the Selenium suite
      // were previously all hidden behind "Show 5 more".
      {
        text: 'Drove unit-test coverage on Java modules from zero to a sustained 80% minimum with JUnit, clearing SonarQube quality-gate failures, code smells, and static-code vulnerabilities in the same pass.',
        tags: ['quality', 'security'],
      },
      {
        text: 'Engineered API components for an RSA sub-project using Core Java, Spring Web, and AWS Kinesis Stream for event-driven processing, writing JUnit coverage for every module delivered.',
        tags: ['backend', 'cloud', 'quality'],
      },
      {
        text: 'Automated regression coverage with Selenium functional test cases across multiple application workflows.',
        tags: ['quality'],
      },
      {
        // Scope and team facts supplied by the candidate 2026-08-27, attributed
        // to TCS after checking the stack: Azure Functions, batch runs and
        // mainframe are all TCS-era, none appear in the current role.
        text: 'Owned 10–12 services spanning 6 web applications, 3–4 REST APIs and shared JAR libraries, together with the Azure batch pipelines and Functions behind them.',
        tags: ['backend', 'cloud', 'delivery'],
      },
      {
        // "Mainframe" deliberately not named: the candidate did no mainframe
        // development, only downstream verification and support around its
        // release windows. Naming it would attract mainframe roles on a
        // keyword match he cannot back.
        text: 'Delivered in an Agile team of 9, later 6, working with an onsite product owner and scrum master, with API releases on the sprint cadence and dependent changes scheduled into windows that protected production batch runs.',
        tags: ['leadership', 'delivery', 'devops'],
      },
      {
        text: 'Monitored production batch runs and collaborated across teams during issue analysis, verifying that backend changes surfaced correctly in the dependent web applications.',
        tags: ['devops', 'quality', 'delivery'],
      },
      {
        text: 'Connected Java applications to DB2 data sources and the Salesforce Marketing Cloud API, configuring enterprise data access and customer-communication workflows surfaced through the Angular web applications.',
        tags: ['backend', 'data', 'delivery'],
      },
    ],
  },
];
